import { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { walletService } from '../../services/wallet/walletService';
import { useSubscription } from '../subscription/SubscriptionContext';
import { downloadWalletCSV } from '../../pages/Wallet/utils/walletHelpers';

const WalletContext = createContext(null);

export const WalletProvider = ({ children }) => {
  const { subscription } = useSubscription();
  const [walletData, setWalletData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRedeemModalOpen, setIsRedeemModalOpen] = useState(false);
  const [expandedTxId, setExpandedTxId] = useState(null);
  const [filterType, setFilterType] = useState('ALL'); // 'ALL' | 'CREDIT' | 'DEBIT'
  const [searchQuery, setSearchQuery] = useState('');

  const userId = localStorage.getItem('user_id');
  const subUserRole = localStorage.getItem('sub_user_role');
  const isSubAdmin = subUserRole === 'subadmin';
  const activePlanSlug = subscription?.plan_id?.slug || (subscription?.status === 'trial' ? 'starter' : 'starter');
  const isStarterPlan = activePlanSlug === 'starter';
  const isGrowthPlan = activePlanSlug === 'growth';
  const isGhostWallet = isSubAdmin || isStarterPlan;

  const fetchBookings = useCallback(async () => {
    try {
      const response = await walletService.getAgentBookings();
      if (response.success) {
        setBookings(response.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
    }
  }, []);

  const fetchWalletDetails = useCallback(async (isSilent = false) => {
    if (!userId) return;
    try {
      if (!isSilent) setIsLoading(true);
      const data = await walletService.getWalletDetails('Agent', userId);
      setWalletData({ balance: data.balance || 0 });
      setTransactions(data.transactions || []);
    } catch (error) {
      console.error('Error fetching wallet:', error);
      if (!isSilent) toast.error('Failed to load wallet details');
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  const toggleExpandTx = (txId) => {
    setExpandedTxId((prev) => (prev === txId ? null : txId));
  };

  const handleDownloadCSV = () => {
    downloadWalletCSV(transactions);
  };

  useEffect(() => {
    if (userId) {
      fetchWalletDetails();
      fetchBookings();

      // Silent real-time auto-refresh interval
      const interval = setInterval(() => {
        fetchWalletDetails(true);
        fetchBookings();
      }, 6000);

      return () => clearInterval(interval);
    }
  }, [userId, fetchWalletDetails, fetchBookings]);

  // Financial Metrics
  const totalLifetimeEarnings = useMemo(() => {
    return transactions
      .filter(
        (tx) =>
          tx.type === 'CREDIT' &&
          (tx.status?.toUpperCase() === 'SUCCESS' ||
            tx.status?.toUpperCase() === 'COMPLETED' ||
            !tx.status)
      )
      .reduce((sum, tx) => sum + (Number(tx.amount) || 0), 0);
  }, [transactions]);

  const totalRedeemed = useMemo(() => {
    return transactions
      .filter(
        (tx) =>
          tx.type === 'DEBIT' &&
          (tx.status?.toUpperCase() === 'SUCCESS' ||
            tx.status?.toUpperCase() === 'COMPLETED' ||
            tx.status?.toUpperCase() === 'PROCESSED')
      )
      .reduce((sum, tx) => sum + (Number(tx.amount) || 0), 0);
  }, [transactions]);

  const totalCompletedBookings = useMemo(() => {
    return transactions.filter((tx) => tx.type === 'CREDIT').length;
  }, [transactions]);

  // Filtered Transactions
  const filteredTransactions = useMemo(() => {
    return transactions.filter((tx) => {
      if (filterType !== 'ALL' && tx.type !== filterType) return false;
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const desc = (tx.description || '').toLowerCase();
        const ref = (tx.razorpayPayoutId || tx.referenceId || '').toLowerCase();
        const amountStr = String(tx.amount || '');
        return desc.includes(query) || ref.includes(query) || amountStr.includes(query);
      }
      return true;
    });
  }, [transactions, filterType, searchQuery]);

  const value = {
    userId,
    walletData,
    transactions,
    bookings,
    isLoading,
    isRedeemModalOpen,
    setIsRedeemModalOpen,
    expandedTxId,
    setExpandedTxId,
    toggleExpandTx,
    filterType,
    setFilterType,
    searchQuery,
    setSearchQuery,
    isSubAdmin,
    isStarterPlan,
    isGrowthPlan,
    isGhostWallet,
    totalLifetimeEarnings,
    totalRedeemed,
    totalCompletedBookings,
    filteredTransactions,
    fetchWalletDetails,
    fetchBookings,
    downloadCSV: handleDownloadCSV
  };

  return <WalletContext.Provider value={value}>{children}</WalletContext.Provider>;
};

export const useWallet = () => {
  const context = useContext(WalletContext);
  if (!context) {
    throw new Error('useWallet must be used within a WalletProvider');
  }
  return context;
};
